// A simple, fast, client-side profanity filter.

// This list is for demonstration. For a production app, this should be
// more comprehensive and could be managed remotely.
const profanityWords: string[] = [
  // Already existing words...
  'ass', 'asses', 'asshole', 'bastard', 'bitch', 'crap', 
  'piss', 'shit', 'porn', 'sex', 'nude', 'xxx', 'dick', 'pussy',

  // Racial slurs (extremely offensive)
  'nigger', 'nigga',
  'chink', 'gook',
  'kike', 'heeb',
  'spic', 'wetback',
  'beaner',
  'sandnigger',
  'raghead',
  'coon',
  'jigaboo',
  'zipperhead',
  'fob',
  'gypsy', // offensive in some contexts
  'paki', // offensive slur for Pakistani people

  // Homophobic/transphobic slurs
  'fag', 'faggot',
  'dyke',
  'tranny',

  // Slurs for disabilities or mental conditions
  'retard', 'retarded',
  'mongoloid',
  'cripple',

  // Offensive religious/ethnic terms
  'infidel',
  'towelhead',
  'zionazi',

  // Add more as needed from vetted hate speech lists
];


// List of blocked domains.
// This is not exhaustive and should be updated regularly.
const blockedDomains: string[] = [
  'pornhub.com',
  'xvideos.com',
  'xnxx.com',
  'livejasmin.com',
  'youporn.com',
  'redtube.com',
  'brazzers.com',
  'xhamster.com',
  'spankbang.com',
  'youjizz.com',
  'beeg.com',
  'tnaflix.com',
  'efukt.com',
  'fapello.com',
  'fux.com',
  'hdporn.com',
  'hqporner.com',
  'motherless.com',
  'rule34.xxx',
  'hclips.com',
  'javhd.com',
  'mydirtyhobby.com',
  'xtube.com',
  'mofos.com',
  'teamskeet.com',
  'vrporn.com',
  'sex.com',
  'onlyfans.com',
  'leakgirls.com',
  'nudogram.com',
  'nudostar.com',
  'cam4.com',
  'chaturbate.com',
  'stripchat.com',
  'camwhores.tv',
  'adultfriendfinder.com',
  'metart.com',
  'fuckbook.com',
  'bangbros.com',
  'evilangel.com',
  'hentaifox.com',
  'hentaihaven.xxx',
  'nhentai.net',
  'hanime.tv',
  'porndude.com',
  'thothub.to',
  'ashemaletube.com',
  'theporndude.com',
];


class ProfanityFilter {
  private wordRegex: RegExp;
  private domainRegex: RegExp;

  constructor(wordList: string[], domainList: string[]) {
    // Regex for individual words with word boundaries to avoid false positives
    const words = new Set(wordList.map(word => word.toLowerCase()));
    this.wordRegex = new RegExp(`\\b(${[...words].join('|')})\\b`, 'gi');

    // Regex for domains. Dots are escaped, and no word boundaries are used
    // to catch domains within a full URL.
    const escapedDomains = domainList.map(domain => domain.replace(/\./g, '\\.'));
    this.domainRegex = new RegExp(`(${escapedDomains.join('|')})`, 'gi');
  }

  /**
   * Checks if the given text contains any profane words or blocked domains.
   * @param text The text to check.
   * @returns True if the text is profane, false otherwise.
   */
  public isProfane(text: string): boolean {
    if (!text) {
      return false;
    }
    const lowerCaseText = text.toLowerCase();
    // Reset regex state before testing
    this.wordRegex.lastIndex = 0; 
    this.domainRegex.lastIndex = 0;
    
    return this.wordRegex.test(lowerCaseText) || this.domainRegex.test(lowerCaseText);
  }

  /**
   * Censors profane words and domains in a string.
   * @param text The text to censor.
   * @param censorChar The character to use for censoring, e.g., '*'.
   * @returns The censored string.
   */
  public censor(text: string, censorChar = '*'): string {
     if (!text) {
      return '';
    }
    // Censor profane words first
    let censoredText = text.replace(this.wordRegex, match => censorChar.repeat(match.length));
    // Then censor blocked domains
    censoredText = censoredText.replace(this.domainRegex, match => censorChar.repeat(match.length));
    return censoredText;
  }
}

// Export a singleton instance for use throughout the app
export const profanityFilter = new ProfanityFilter(profanityWords, blockedDomains);